import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  List,
  Globe,
  Shield
} from 'lucide-react';

const SelectionValuesManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // State for all selection values
  const [listValues, setListValues] = useState(null);
  const [overrides, setOverrides] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState('lead');
  const [selectedField, setSelectedField] = useState(null);
  
  // Dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingValues, setEditingValues] = useState([]);
  const [editingField, setEditingField] = useState(null);
  
  // Default values state
  const [defaultValues, setDefaultValues] = useState({});
  const [editingDefaults, setEditingDefaults] = useState(false);
  
  // Active tab state
  const [activeTab, setActiveTab] = useState('selection-values');
  
  // Available entities
  const entities = [
    { value: 'lead', label: 'Lead' },
    { value: 'customer', label: 'Customer' },
    { value: 'opportunity', label: 'Opportunity' },
    { value: 'activity', label: 'Activity' }
  ];

  // Load selection values on mount
  useEffect(() => {
    loadListValues();
    loadDefaultValues();
  }, []);

  const loadListValues = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('Loading tenant selection values with user:', {
        userId: user.userId,
        userType: user.userType,
        tenantCode: user.tenantCode,
        hasToken: !!token
      });
      
      const response = await fetch('/api/tenant-selection', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Tenant selection values response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Tenant API Error:', errorText);
        throw new Error(`Failed to load selection values: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Received tenant data:', data);
      
      if (data.success) {
        setListValues(data.selectionValues || data.listValues);
        setOverrides(data.overrides || {});
        
        // Set default field if available
        const values = data.selectionValues || data.listValues;
        if (!selectedField && values && values[selectedEntity]) {
          const fields = Object.keys(values[selectedEntity]);
          if (fields.length > 0) {
            setSelectedField(fields[0]);
          }
        }
      } else {
        throw new Error(data.message || 'Failed to load selection values');
      }
    } catch (err) {
      console.error('Error loading selection values:', err);
      setError(err.message || 'Failed to load selection values');
    } finally {
      setLoading(false);
    }
  };

  const handleEntityChange = (event) => {
    const newEntity = event.target.value;
    setSelectedEntity(newEntity);
    
    // Reset field selection
    if (listValues && listValues[newEntity]) {
      const fields = Object.keys(listValues[newEntity]);
      setSelectedField(fields.length > 0 ? fields[0] : null);
    } else {
      setSelectedField(null);
    }
  };

  const handleEditField = (fieldName) => {
    const fieldData = listValues[selectedEntity][fieldName];
    setEditingField(fieldName);
    setEditingValues(fieldData.values || []);
    setEditDialogOpen(true);
  };

  const handleAddNewValue = () => {
    setEditingValues([
      ...editingValues,
      {
        value: '',
        label: '',
        order: editingValues.length + 1,
        color: '',
        icon: ''
      }
    ]);
  };

  const handleUpdateValue = (index, field, value) => {
    const updated = [...editingValues];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setEditingValues(updated);
  };

  const handleDeleteValue = (index) => {
    const updated = editingValues.filter((_, i) => i !== index);
    // Reorder remaining items
    updated.forEach((item, i) => {
      item.order = i + 1;
    });
    setEditingValues(updated);
  };

  const handleMoveValue = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === editingValues.length - 1)
    ) {
      return;
    }

    const updated = [...editingValues];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Swap items
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    
    // Update order numbers
    updated.forEach((item, i) => {
      item.order = i + 1;
    });
    
    setEditingValues(updated);
  };

  const handleSaveValues = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validate values
      for (const val of editingValues) {
        if (!val.value || !val.label) {
          setError('All values must have both "value" and "label" fields');
          setSaving(false);
          return;
        }
      }

      const response = await fetch(
        `/api/tenant-selection/${selectedEntity}/${editingField}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ values: editingValues })
        }
      );

      if (!response.ok) throw new Error('Failed to save values');
      
      const data = await response.json();

      if (data.success) {
        setSuccess(`Successfully updated ${editingField} values`);
        setEditDialogOpen(false);
        loadListValues(); // Reload data
      }
    } catch (err) {
      console.error('Error saving values:', err);
      setError(err.message || 'Failed to save values');
    } finally {
      setSaving(false);
    }
  };

  const handleResetField = async (fieldName) => {
    if (!window.confirm(`Are you sure you want to reset ${fieldName} to global defaults?`)) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await fetch(
        `/api/tenant-selection/${selectedEntity}/${fieldName}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to reset field');
      
      const data = await response.json();

      if (data.success) {
        setSuccess(`Successfully reset ${fieldName} to global defaults`);
        loadListValues();
      }
    } catch (err) {
      console.error('Error resetting field:', err);
      setError(err.message || 'Failed to reset field');
    } finally {
      setSaving(false);
    }
  };

  const loadDefaultValues = async () => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/settings/defaults`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch default values');
      const data = await response.json();
      setDefaultValues(data.defaults || {});
    } catch (err) {
      console.error('Error loading default values:', err);
    }
  };

  const handleSaveDefaults = async () => {
    try {
      setSaving(true);
      setError(null);
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/settings/defaults`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ defaults: defaultValues })
      });

      if (!response.ok) throw new Error('Failed to save default values');
      
      setSuccess('Default values saved successfully');
      setEditingDefaults(false);
    } catch (err) {
      console.error('Error saving default values:', err);
      setError(err.message || 'Failed to save default values');
    } finally {
      setSaving(false);
    }
  };

  const handleDefaultValueChange = (key, value) => {
    setDefaultValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!listValues) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2" />
            <div>
              <h3 className="text-yellow-800 dark:text-yellow-200 font-semibold">No Selection Values Loaded</h3>
              <p className="text-yellow-700 dark:text-yellow-300 text-sm mt-1">
                {error || 'Failed to load selection values. Please try refreshing the page.'}
              </p>
            </div>
          </div>
          <button
            onClick={loadListValues}
            className="mt-3 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const currentEntityData = listValues?.[selectedEntity] || {};
  const availableFields = Object.keys(currentEntityData);
  
  // Check if current field has tenant override
  const hasOverride = overrides?.[selectedEntity]?.[selectedField] ? true : false;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Selection Values & Defaults Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage dropdown selection options and default values for your entity fields.
        </p>
        <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            ℹ️ Manage dropdown options for your entity fields. Create tenant-specific overrides or use global defaults.
          </p>
          <button
            onClick={async () => {
              try {
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                const token = localStorage.getItem('token');
                console.log('Current user info:', user);
                alert(`User Info: Type=${user.userType}, Tenant=${user.tenantCode}, Has Token=${!!token}`);
              } catch (err) {
                console.error('Debug error:', err);
                alert('Debug failed: ' + err.message);
              }
            }}
            className="mt-2 px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
          >
            Debug User Context
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-3 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start">
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-3 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
          </div>
          <button
            onClick={() => setSuccess(null)}
            className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('selection-values')}
            className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'selection-values'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <List className="h-4 w-4" />
            <span>Selection Values</span>
          </button>
          <button
            onClick={() => setActiveTab('defaults')}
            className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'defaults'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <CheckCircle className="h-4 w-4" />
            <span>Default Values</span>
          </button>
        </nav>
      </div>

      {activeTab === 'selection-values' && (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Entity Selector Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Select Entity
            </h2>
            
            <select
              value={selectedEntity}
              onChange={handleEntityChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              {entities.map((entity) => (
                <option key={entity.value} value={entity.value}>
                  {entity.label}
                </option>
              ))}
            </select>

            {availableFields.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fields with Custom Values:
                </h3>
                <div className="space-y-1">
                  {availableFields.map((field) => (
                    <button
                      key={field}
                      onClick={() => setSelectedField(field)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                        selectedField === field
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {field}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Field Values Display */}
        <div className="lg:col-span-3">
          {selectedField && currentEntityData[selectedField] ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                      {selectedField} Values
                      {hasOverride ? (
                        <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                          <Shield className="h-3 w-3 mr-1" />
                          Tenant Override
                        </span>
                      ) : (
                        <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                          <Globe className="h-3 w-3 mr-1" />
                          Global Default
                        </span>
                      )}
                    </h2>
                    {!hasOverride && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Using global defaults set by Super Admin. Click "Edit Values" to create a tenant-specific override.
                      </p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    {hasOverride && (
                      <button
                        onClick={() => handleResetField(selectedField)}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <RefreshCw className="h-4 w-4 inline mr-2" />
                        Reset to Global
                      </button>
                    )}
                    <button
                      onClick={() => handleEditField(selectedField)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Edit className="h-4 w-4 inline mr-2" />
                      {hasOverride ? 'Edit Override' : 'Create Override'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Order
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Value
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Label
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Color
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Icon
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {currentEntityData[selectedField].values.map((val, index) => (
                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                            {val.order}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <code className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-gray-800 dark:text-gray-200">
                              {val.value}
                            </code>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                            {val.label}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {val.color && (
                              <span
                                className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium text-white"
                                style={{ backgroundColor: val.color }}
                              >
                                {val.color}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {val.icon || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-12 text-center">
              <List className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                No custom selection values configured for this entity.
                <br />
                Select a field from the list or create a new one.
              </p>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Default Values Tab */}
      {activeTab === 'defaults' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Default Values</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Set default values for new records across different entities
              </p>
            </div>
            <div className="flex space-x-2">
              {editingDefaults ? (
                <>
                  <button
                    onClick={() => setEditingDefaults(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveDefaults}
                    disabled={saving}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        <span>Save</span>
                      </>
                    )}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditingDefaults(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Edit className="h-4 w-4" />
                  <span>Edit</span>
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Default Customer Type
              </label>
              <select
                value={defaultValues.customerType || ''}
                onChange={(e) => handleDefaultValueChange('customerType', e.target.value)}
                disabled={!editingDefaults}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-50 dark:disabled:bg-gray-800"
              >
                <option value="">Select option</option>
                <option value="Individual">Individual</option>
                <option value="Company">Company</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Default Country
              </label>
              <input
                type="text"
                value={defaultValues.country || ''}
                onChange={(e) => handleDefaultValueChange('country', e.target.value)}
                disabled={!editingDefaults}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-50 dark:disabled:bg-gray-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Default Currency
              </label>
              <select
                value={defaultValues.currency || ''}
                onChange={(e) => handleDefaultValueChange('currency', e.target.value)}
                disabled={!editingDefaults}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-50 dark:disabled:bg-gray-800"
              >
                <option value="">Select option</option>
                <option value="USD">USD</option>
                <option value="THB">THB</option>
                <option value="EUR">EUR</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Default Lead Source
              </label>
              <input
                type="text"
                value={defaultValues.leadSource || ''}
                onChange={(e) => handleDefaultValueChange('leadSource', e.target.value)}
                disabled={!editingDefaults}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-50 dark:disabled:bg-gray-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Default Opportunity Stage
              </label>
              <input
                type="text"
                value={defaultValues.opportunityStage || ''}
                onChange={(e) => handleDefaultValueChange('opportunityStage', e.target.value)}
                disabled={!editingDefaults}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-50 dark:disabled:bg-gray-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Default Contract Status
              </label>
              <input
                type="text"
                value={defaultValues.contractStatus || ''}
                onChange={(e) => handleDefaultValueChange('contractStatus', e.target.value)}
                disabled={!editingDefaults}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-50 dark:disabled:bg-gray-800"
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      {editDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Edit {editingField} Values
              </h2>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-4">
                <button
                  onClick={handleAddNewValue}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <Plus className="h-4 w-4 inline mr-2" />
                  Add New Value
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Order
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Value *
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Label *
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Color
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Icon
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {editingValues.map((val, index) => (
                      <tr key={index}>
                        <td className="px-2 py-3">
                          <div className="flex items-center space-x-1">
                            <span className="text-sm text-gray-700 dark:text-gray-300">{val.order}</span>
                            <div className="flex flex-col">
                              <button
                                onClick={() => handleMoveValue(index, 'up')}
                                disabled={index === 0}
                                className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-30"
                              >
                                <ArrowUp className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleMoveValue(index, 'down')}
                                disabled={index === editingValues.length - 1}
                                className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-30"
                              >
                                <ArrowDown className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-3">
                          <input
                            type="text"
                            value={val.value}
                            onChange={(e) => handleUpdateValue(index, 'value', e.target.value)}
                            placeholder="e.g., High"
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </td>
                        <td className="px-2 py-3">
                          <input
                            type="text"
                            value={val.label}
                            onChange={(e) => handleUpdateValue(index, 'label', e.target.value)}
                            placeholder="e.g., High Priority"
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </td>
                        <td className="px-2 py-3">
                          <input
                            type="text"
                            value={val.color || ''}
                            onChange={(e) => handleUpdateValue(index, 'color', e.target.value)}
                            placeholder="e.g., red"
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-2 py-3">
                          <input
                            type="text"
                            value={val.icon || ''}
                            onChange={(e) => handleUpdateValue(index, 'icon', e.target.value)}
                            placeholder="e.g., ArrowUp"
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-2 py-3 text-center">
                          <button
                            onClick={() => handleDeleteValue(index)}
                            className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setEditDialogOpen(false)}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                <X className="h-4 w-4 inline mr-2" />
                Cancel
              </button>
              <button
                onClick={handleSaveValues}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Values
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectionValuesManagement;
